import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { fetchUserData } from 'store/slice/common';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import { getEmployeeHistoryReportApi, exportEmployeeHistoryReportApi, uniformErrorMessage } from 'api/services/uniformManagement';
import { itemRender, handlePageChange, defaultPagination, pageSizeOptions } from 'utils/table';
import { formatDateHandler } from 'utils/common';
import { getStatusBadge, getGenderLabel, issuanceStatusOptions } from '../../constants';
import ExcelExportButton from 'components/ExcelExportButton';
import ReportFilterBar, { cleanParams } from '../ReportFilterBar';
import MasterDataRetryAlert from '../../components/MasterDataRetryAlert';
import useStickyIdentity from '../../components/useStickyIdentity';
import { UNIFORM_LANE, truncatedText } from '../../components/columns';

const toastOpts = { position: 'top-right', autoClose: 3000, transition: Bounce };

/** `items` (plural) is the multi-select variant — this report's uniform-item
 *  filter takes a CSV list. The employee is NOT a filter here: it is a path
 *  segment on the endpoint (`/reports/employee-history/{id}`), so this report
 *  is by construction about exactly one person and its picker stays single. */
const FIELDS = ['dateRange', 'items', 'status'];

/** One employee's issuance history. Same row shape/filters as the issuance
 *  report (server now returns the paginated {count, results} envelope —
 *  it used to return {status, data: []}), scoped to a single employee. */
const EmployeeHistoryReport = ({ itemOptions, itemOptionsLoading }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userData, userDataLoading, userDataError } = useSelector((state) => state.common);

  const employeeOptions = (userData || [])
    .slice()
    .sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '', undefined, { sensitivity: 'base' }))
    .map((u) => ({
      label: `${u.first_name} ${u.last_name}${u.emp_no ? ` (${u.emp_no})` : ''}`,
      value: u.id, emp_no: u.emp_no || '',
    }));

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSettings, setPageSettings] = useState({ ...defaultPagination });
  const [draft, setDraft] = useState({});
  const [applied, setApplied] = useState({});

  // Guarded with `ignore` so a slow response for an employee/filter set the
  // user has since changed away from can't clobber newer results.
  const fetchData = useCallback(() => {
    if (!selectedEmployee) {
      setData([]);
      return undefined;
    }
    let ignore = false;
    setLoading(true);
    const params = { ...cleanParams(applied), page: pageSettings.page, page_size: pageSettings.pageSize };
    getEmployeeHistoryReportApi(selectedEmployee.value, params).then((response) => {
      if (ignore) return;
      setData(response.data?.results || []);
      setPageSettings((prev) => ({ ...prev, total: response.data?.count || 0 }));
    }).catch((e) => {
      if (ignore) return;
      setData([]);
      toast.error(uniformErrorMessage(e, 'Failed to load employee history'), toastOpts);
    }).finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [selectedEmployee, applied, pageSettings.page, pageSettings.pageSize]);

  useEffect(() => fetchData(), [fetchData]);

  const handleChange = (pagination, filters, sorter) => {
    setPageSettings(handlePageChange(pagination, filters, sorter, pageSettings));
  };

  const handleSelectEmployee = (option) => {
    setSelectedEmployee(option);
    setDraft({});
    setApplied({});
    setPageSettings((prev) => ({ ...prev, page: 1 }));
  };

  const handleApply = () => {
    setApplied(draft);
    setPageSettings((prev) => ({ ...prev, page: 1 }));
  };

  const handleClear = () => {
    setDraft({});
    setApplied({});
    setPageSettings((prev) => ({ ...prev, page: 1 }));
  };

  const pinLeft = useStickyIdentity();

  const columns = [
    // Reference leads because it is the identity. A fixed-left run has to start
    // at column 0, so pinning Reference means Reference must BE column 0 —
    // pinning it in place at index 1 would set rc-table's `hasGapFixed` and
    // antd would then force `box-shadow: none` on the lane (table/style/fixed.js),
    // leaving a column that silently overlaps scrolled content with no edge.
    // Reference-first also matches every other list in the module; Date-first
    // was the outlier here. The Excel export is server-side, so column order is
    // a front-end concern only.
    {
      title: 'Reference', dataIndex: 'batch_reference', ...pinLeft(170),
      render: (ref, record) => ref ? (
        record.batch ? (
          <button type="button" className="btn btn-link p-0" style={{ color: '#D3001A' }}
            onClick={() => navigate(`/uniform-management/issuance/batches/${record.batch}`)}>
            {ref}
          </button>
        ) : ref
      ) : '-'
    },
    { title: 'Date', dataIndex: 'issuance_date', render: (d) => formatDateHandler(d) },
    { title: 'Cost Center', dataIndex: 'casa_name', render: (v) => v || '-' },
    { title: 'Employee ID', dataIndex: 'company_user_emp_no' },
    { title: 'Position', dataIndex: 'position_name', render: (v) => v || '-' },
    { title: 'Gender', dataIndex: 'gender', render: (g) => getGenderLabel(g) },
    { title: 'Uniform', dataIndex: 'uniform_item_name', ...truncatedText(UNIFORM_LANE) },
    { title: 'Size', dataIndex: 'size_code', render: (v) => v || '-' },
    { title: 'Qty', dataIndex: 'quantity' },
    { title: 'Warehouse', dataIndex: 'warehouse_name' },
    { title: 'Issued By', dataIndex: 'issued_by_name', render: (v) => v || '-' },
    {
      title: 'Next Replacement', dataIndex: 'next_replacement_date',
      render: (d) => d ? formatDateHandler(d) : '-'
    },
    { title: 'Status', dataIndex: 'status', render: (s) => <span style={getStatusBadge(s)}>{s}</span> },
  ];

  return (
    <div>
      {/* The employee picker is the only way to produce this report, so an
          empty one is not a lost filter — it is a report that can never run. */}
      <MasterDataRetryAlert
        show={userDataError && !userDataLoading}
        onRetry={() => dispatch(fetchUserData())}
      >
        The employee list could not be loaded, so no employee can be selected and this report
        cannot be run.
      </MasterDataRetryAlert>

      <div className="row mb-3 align-items-end">
        <div className="col-md-4">
          <label className="col-form-label d-block" style={{ fontSize: 12 }}>Employee</label>
          <FilterSelect isClearable onChange={handleSelectEmployee} optionLabel="label" optionValue="value"
            options={employeeOptions} value={selectedEmployee} placeholder="Select Employee" isLoading={userDataLoading} />
        </div>
        {selectedEmployee && (
          <div className="col-md-2">
            <Button label="View Card" icon="fa fa-id-card" className="btn btn-outline-primary btn-sm"
              onClick={() => navigate(`/uniform-management/employee-card/${selectedEmployee.value}`)} />
          </div>
        )}
        {selectedEmployee && (
          <div className="col-md-3 ms-auto text-end">
            <ExcelExportButton framed
              apiFn={(params) => exportEmployeeHistoryReportApi(selectedEmployee.value, params)}
              params={cleanParams(applied)}
              filename={`Uniform History_${selectedEmployee.emp_no || selectedEmployee.value}.xlsx`}
            />
          </div>
        )}
      </div>

      {selectedEmployee ? (
        <>
          <ReportFilterBar
            fields={FIELDS}
            value={draft} onChange={setDraft} statusOptions={issuanceStatusOptions}
            itemOptions={itemOptions} itemOptionsLoading={itemOptionsLoading}
            onApply={handleApply} onClear={handleClear}
          />
          <Table columns={columns} dataSource={data} rowKey={(r) => r.id} loading={loading}
            pagination={{
              total: pageSettings.total, pageSize: pageSettings.pageSize, current: pageSettings.page,
              pageSizeOptions, itemRender, showSizeChanger: true
            }}
            onChange={handleChange} scroll={{ x: 'max-content' }} />
        </>
      ) : (
        <div className="text-center py-5 text-muted">Select an employee to view their issuance history</div>
      )}
    </div>
  );
};

export default EmployeeHistoryReport;
